import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { formatDate } from "../utils/datetime";

/**
 * MSP-wide licence renewal summary. Every customer's licences expiring
 * within N days, plus per-severity tile counts. The per-customer licences
 * card is for one customer at a time; this is the "show me everything I
 * need to renew" view.
 *
 * NO VENDOR NAMES IN THE HEADINGS OR THE TILES. An MSP reselling AeroGauge
 * may use a different controller entirely, so the copy describes what the
 * data IS (licences pulled from configured network controller integrations)
 * rather than naming the vendor whose API happens to feed it. The file name
 * still says Meraki because renaming a route is a separate, riskier change
 * - see the task about unifying this screen with manually entered licences.
 *
 * IT COVERS BOTH SOURCES. The renewal sweep acts on licences pulled from a
 * controller API and on licences an operator typed in by hand for a vendor
 * with no API; both alert, both hit the calendar, both open a renewal
 * ticket. This screen used to list the pulled kind only, so a hand-entered
 * licence was invisible on the one page built for renewal planning and the
 * tiles undercounted. The `source` field says which is which.
 */

type Row = {
  /** "pulled-12" / "manual-3" — the two tables number independently, so a
   *  bare integer collides between sources. */
  id: string;
  source: "pulled" | "manual";
  customer_id: number;
  customer_name: string;
  /** Only the hand-entered kind carries a vendor: a pulled row belongs to an
   *  integration, not to a name somebody typed. */
  vendor: string | null;
  product: string | null;
  /** The vendor's own licence id, for correlating with their portal. Null for
   *  a hand-entered row — nothing issued one. */
  license_id: string | null;
  identifier: string | null;
  reference_suffix: string | null;
  state: string | null;
  seat_count: number | null;
  annual_cost_cents: number | null;
  expiration_date: string;
  days_remaining: number;
  severity: "ok" | "warning" | "critical" | "expired";
  calendar_event_id: number | null;
  renewal_ticket_id: number | null;
  last_synced_at: string | null;
};

type Summary = {
  as_of: string;
  total_tracked: number;
  customers_with_meraki: number;
  expired_recent_90d: number;
  critical_7d_or_less: number;
  warning_30d: number;
  ok_beyond_30d: number;
};

const SEV_PILL: Record<string, string> = {
  ok: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  warning: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-300",
  critical: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  expired: "bg-red-200 text-red-900 font-bold dark:bg-red-900/50 dark:text-red-200",
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return formatDate(iso);
}

export default function MerakiLicenseSummary() {
  const [rows, setRows] = useState<Row[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [withinDays, setWithinDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const rowSort = useSort("days_remaining", "asc");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly - credential and
  // machine-output fields must not reach a prompt.
  usePublishScreenData(
    "Cloud licence summary",
    useMemo(
      () =>
        rows.map((r) => ({
          customer: r.customer_name,
          source: r.source,
          status: (r as any).status ?? null,
          expires: (r as any).expiration_date ?? null,
          days_remaining: (r as any).days_remaining ?? null,
          devices: (r as any).device_count ?? null,
        })),
      [rows],
    ),
    undefined,
  );

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const [expRes, sumRes] = await Promise.all([
        api.get("/api/v1/meraki-licenses/expiring", {
          params: { within_days: withinDays },
        }),
        api.get("/api/v1/meraki-licenses/summary"),
      ]);
      setRows(expRes.data || []);
      setSummary(sumRes.data || null);
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Failed to load license summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withinDays]);

  // Group rows by severity for display
  const grouped: Record<string, Row[]> = {
    expired: [],
    critical: [],
    warning: [],
    ok: [],
  };
  for (const r of rows) grouped[r.severity]?.push(r);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Licence renewals
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Every licence expiring across all customers, pulled daily at 6:00 AM
            ET from your configured network controller integrations. The same
            data drives each customer's licences tab and the calendar reminders
            at 30 days.
          </p>
        </div>
        <label className="text-sm shrink-0">
          <span className="block text-gray-600 dark:text-gray-300 mb-1">
            Show within
          </span>
          <select
            value={withinDays}
            onChange={(e) => setWithinDays(Number(e.target.value))}
            className="border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days (recommended)</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
            <option value={365}>1 year</option>
          </select>
        </label>
      </div>

      {/* KPI tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Tile label="Tracked licences" value={summary.total_tracked} />
          <Tile
            label="Customers tracked"
            value={summary.customers_with_meraki}
          />
          <Tile
            label="Expired (last 90d)"
            value={summary.expired_recent_90d}
            accent={summary.expired_recent_90d > 0 ? "danger" : "ok"}
          />
          <Tile
            label="≤ 7 days"
            value={summary.critical_7d_or_less}
            accent={summary.critical_7d_or_less > 0 ? "danger" : "ok"}
          />
          <Tile
            label="≤ 30 days"
            value={summary.warning_30d}
            accent={summary.warning_30d > 0 ? "warn" : "ok"}
          />
        </div>
      )}

      {err && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
          {err}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-8 text-center">
          <p className="text-sm text-gray-700 dark:text-gray-200 font-medium mb-1">
            No licences expiring within {withinDays} days.
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Either every customer's licensing is healthy, or nothing is being
            tracked yet. Controller integrations are added under{" "}
            <Link to="/network-discovery" className="text-brand dark:text-brand-light underline">
              Discovery &amp; integrations
            </Link>
            ; anything without an API goes on the customer's Licences card by
            hand and is watched the same way.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {(["expired", "critical", "warning", "ok"] as const).map((sev) => {
            if (grouped[sev].length === 0) return null;
            const labels: Record<string, string> = {
              expired: "Expired",
              critical: "Critical — 7 days or less",
              warning: "Warning — 30 days",
              ok: "Beyond 30 days",
            };
            return (
              <div
                key={sev}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden"
              >
                <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    {labels[sev]}
                  </h2>
                  <span
                    className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${SEV_PILL[sev]}`}
                  >
                    {grouped[sev].length}
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400 uppercase">
                    <tr>
                      <SortableTh label="Customer" sortKey="customer_name" sort={rowSort.sort} onSort={rowSort.toggle} />
                      <SortableTh label="Licence" sortKey="product" sort={rowSort.sort} onSort={rowSort.toggle} />
                      <SortableTh label="Identifier" sortKey="identifier" sort={rowSort.sort} onSort={rowSort.toggle} />
                      <SortableTh label="Expires" sortKey="expiration_date" sort={rowSort.sort} onSort={rowSort.toggle} />
                      <SortableTh label="Days" sortKey="days_remaining" sort={rowSort.sort} onSort={rowSort.toggle} />
                      <th className="text-left px-3 py-2 font-medium">Open</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {sortRows(grouped[sev], rowSort.sort, (r, key) => {
                      switch (key) {
                        case "customer_name": return (r.customer_name || "").toLowerCase();
                        case "product": return (r.product || "").toLowerCase();
                        case "identifier": return (r.identifier || "").toLowerCase();
                        case "expiration_date": return r.expiration_date ? Date.parse(r.expiration_date) : 0;
                        default: return r.days_remaining;
                      }
                    }).map((r) => (
                      <tr
                        key={r.id}
                        className="text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-900"
                      >
                        <td className="px-3 py-2 font-medium">
                          <Link
                            to={`/customers/${r.customer_id}`}
                            className="text-brand dark:text-brand-light hover:underline"
                          >
                            {r.customer_name}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div className="flex items-center gap-1.5">
                            <span>
                              {[r.vendor, r.product].filter(Boolean).join(" ") || "—"}
                            </span>
                            {/* Where the row came from, because it changes
                                what the operator can do about it: a pulled
                                licence fixes itself on the next sync, a
                                hand-entered one needs the date editing. */}
                            <span
                              className={
                                r.source === "manual"
                                  ? "text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300"
                                  : "text-[10px] px-1.5 py-0.5 rounded-full bg-brand/10 text-brand dark:bg-brand-light/15 dark:text-brand-light"
                              }
                              title={
                                r.source === "manual"
                                  ? "Entered by hand — edit it on the customer's Licences card"
                                  : "Pulled from a controller integration on the daily sync"
                              }
                            >
                              {r.source === "manual" ? "manual" : "synced"}
                            </span>
                          </div>
                          {r.reference_suffix && (
                            <div className="font-mono text-[10px] text-gray-500 dark:text-gray-400">
                              …{r.reference_suffix}
                            </div>
                          )}
                          {r.seat_count != null && (
                            <div className="text-[10px] text-gray-500 dark:text-gray-400">
                              {r.seat_count} seat{r.seat_count === 1 ? "" : "s"}
                              {r.annual_cost_cents != null &&
                                ` · $${(r.annual_cost_cents / 100).toLocaleString()}/yr`}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono">
                          {r.identifier || (
                            <span className="italic font-sans text-gray-400 dark:text-gray-500">
                              {r.source === "pulled" ? "co-term" : "—"}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {fmtDate(r.expiration_date)}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {r.days_remaining < 0
                            ? `${Math.abs(r.days_remaining)}d ago`
                            : `${r.days_remaining}d`}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <Link
                            to={`/customers/${r.customer_id}?tab=${
                              r.source === "manual" ? "licenses" : "meraki_licenses"
                            }`}
                            className="text-brand dark:text-brand-light hover:underline mr-3"
                          >
                            Licences
                          </Link>
                          {/* The sweep opens a renewal ticket; linking to it
                              is the difference between knowing and doing. */}
                          {r.renewal_ticket_id && (
                            <Link
                              to={`/tickets/${r.renewal_ticket_id}`}
                              className="text-brand dark:text-brand-light hover:underline mr-3"
                            >
                              Ticket
                            </Link>
                          )}
                          {r.calendar_event_id && (
                            <Link
                              to="/calendar"
                              className="text-brand dark:text-brand-light hover:underline"
                            >
                              Calendar
                            </Link>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {summary && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500 text-right">
          As of {new Date(summary.as_of).toLocaleString()}
        </p>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: "ok" | "warn" | "danger";
}) {
  const color =
    accent === "danger"
      ? "text-red-700 dark:text-red-300"
      : accent === "warn"
        ? "text-amber-700 dark:text-amber-300"
        : "text-gray-900 dark:text-gray-100";
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-3">
      <div className="text-[11px] text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-3xl font-bold mt-0.5 font-mono ${color}`}>
        {value}
      </div>
    </div>
  );
}
